// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import type { InputHTMLAttributes, ReactNode, Ref } from 'react';

import styles from './Checkbox.module.css';
import { cx } from './cx';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  /** With a label the box sits in a `<label>` row; without, it is a bare box (table cells — pass `aria-label`). */
  label?: ReactNode;
  /** Small text at the row's end (a count, "indlæses…"). Needs `label`. */
  hint?: ReactNode;
  /** Muted label text (toolbar filters, rows with nothing behind them). */
  muted?: boolean;
  ref?: Ref<HTMLInputElement>;
}

/**
 * The shared checkbox: a native box in the accent colour. With `label`,
 * `className` and `style` land on the `<label>` row; without, on the
 * `<input>`. Every other prop goes to the `<input>`.
 */
export function Checkbox({ label, hint, muted, className, style, ...rest }: CheckboxProps) {
  if (label === undefined || label === null || label === false) {
    return <input type="checkbox" className={cx(styles.box, className)} style={style} {...rest} />;
  }
  return (
    <label
      className={cx(styles.root, className)}
      style={style}
      data-disabled={rest.disabled || undefined}
      data-muted={muted || undefined}
    >
      <input type="checkbox" className={styles.box} {...rest} />
      <span className={styles.text}>{label}</span>
      {hint != null && hint !== false && <small className={styles.hint}>{hint}</small>}
    </label>
  );
}
